import { useDisclosure } from "@mantine/hooks";
import { t } from "i18next";
import { useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { computationOptions } from "#app/computationOptions";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { AddSectionHeader } from "#modules/quoteRequestLines/views/DetailsPanel/AddSectionHeader";
import { CostCollapsable } from "#modules/quoteRequestLines/views/DetailsPanel/CostCollapsable";
import { Products } from "#modules/quoteRequestLines/views/DetailsPanel/Products/Products";
import { useProductLinesDirtyState } from "#modules/quoteRequestLines/views/DetailsPanel/Products/useProductLineDirtyState";
import { computeQuoteRequestLinePrice, QuoteRequestLineDto } from "shared";

import { OutsourcingSection } from "../OutsourcingSection";

export const ProductsSection = ({
  quoteRequestLine,
}: {
  quoteRequestLine?: QuoteRequestLineDto;
}) => {
  const { control } = useFormContext<QuoteRequestLineFormData>();
  const {
    append,
    remove,
    insert,
    fields: productLines,
  } = useFieldArray<QuoteRequestLineFormData, "productLines", "__rhfId">({
    control,
    name: "productLines",
    keyName: "__rhfId",
  });
  const [
    isOutSourcingSelectDisplayed,
    { open: showOutSourcingSelect, close: hideOutSourcingSelect },
  ] = useDisclosure(false);

  const { isAnyProductDirty } = useProductLinesDirtyState();

  const addProduct = () => {
    append({
      quantity: 1,
      isQuantityPerPart: true,
      name: "",
      price: 0,
    });
  };

  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequestLine?.quoteRequestId);
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequestLine?.quoteRequestId);
  const quoteRequestLinePrice = useMemo(
    () =>
      quoteRequestLine
        ? computeQuoteRequestLinePrice(
            {
              quoteRequestLine,
              optimizedMachineTimes,
              optimizedMaterialCosts,
            },
            computationOptions,
          )
        : null,
    [quoteRequestLine, optimizedMachineTimes, optimizedMaterialCosts],
  );

  return (
    <CostCollapsable
      title={t("quoteRequestLine.purchases.cost")}
      value={quoteRequestLinePrice?.costs.partProductsProcessesCost ?? 0}
      data-testid="products-section"
    >
      <AddSectionHeader
        label={t("quoteRequestLine.purchases.forPart")}
        onAdd={addProduct}
        shouldDisplayAddButton={!isAnyProductDirty}
      />

      <Products productLines={productLines} removeProduct={remove} insertProduct={insert} />
      <AddSectionHeader
        label={t("quoteRequestLine.purchases.outsourcing")}
        onAdd={showOutSourcingSelect}
        shouldDisplayAddButton
      />
      <OutsourcingSection
        processStepSelectIsDisplayed={isOutSourcingSelectDisplayed}
        hideProcessStepSelect={hideOutSourcingSelect}
        quoteRequestLine={quoteRequestLine}
      />
    </CostCollapsable>
  );
};
